import * as THREE from 'three'

import Experience from '.'

import blueSquiggleSrc from '../assets/squiggle/blueSquiggle.png'
import greenSquiggleSrc from '../assets/squiggle/greenSquiggle.png'
import linesSquiggleSrc from '../assets/squiggle/linesSquiggle.png'
import lines2SquiggleSrc from '../assets/squiggle/lines2Squiggle.png'
import orangeSquiggleSrc from '../assets/squiggle/orangeSquiggle.png'
import pinkSquiggleSrc from '../assets/squiggle/pinkSquiggle.png'
import pink2SquiggleSrc from '../assets/squiggle/pink2Squiggle.png'
// import yellowSquiggleSrc from '../assets/squiggle/yellowSquiggle.png'
import LoadingManager from './Utils/LoadingManager'

export default class Squiggles {
  static instance: Squiggles

  private experience = new Experience()

  public group = new THREE.Group()

  public yellowSquiggle!: THREE.Sprite

  constructor(private loader = new THREE.TextureLoader(LoadingManager)) {
    if (Squiggles.instance) {
      return Squiggles.instance
    }

    this.init()
    this.hide()
  }

  public show() {
    this.group.visible = true
  }

  public hide() {
    this.group.visible = false

    this.group.children.forEach((item) => {
      const squiggle = item as THREE.Sprite
      const material = squiggle.material as THREE.SpriteMaterial
      material.opacity = 0
    })
  }

  private applyTextureEncoding(texture: THREE.Texture) {
    texture.encoding = THREE.sRGBEncoding
    texture.format = THREE.RGBAFormat
  }

  private init() {
    const {
      sceneOrtho,
      config: {
        screen: { pixelRatio },
      },
    } = this.experience

    sceneOrtho.add(this.group)

    const wWidth = window.innerWidth / 2
    const wHeight = window.innerHeight / 2

    const squiggleMaterial = new THREE.SpriteMaterial({
      transparent: true,
      opacity: 0,
    })

    // Blue
    this.loader.load(blueSquiggleSrc, (texture) => {
      this.applyTextureEncoding(texture)

      const blueSquiggleMaterial = squiggleMaterial.clone() //new THREE.SpriteMaterial({ map: texture })
      blueSquiggleMaterial.map = texture

      const { width, height } = blueSquiggleMaterial.map?.image || {
        width: 0,
        height: 0,
      }

      const scaleOffset = 0.5

      const blueSquiggle = new THREE.Sprite(blueSquiggleMaterial)
      blueSquiggle.center.set(1.0, 1.0)
      blueSquiggle.scale.set(
        width / (pixelRatio - scaleOffset),
        height / (pixelRatio - scaleOffset),
        1
      )
      blueSquiggle?.position.set(wWidth, wHeight + 15, 1)

      this.group.add(blueSquiggle)
    })

    // Green
    this.loader.load(greenSquiggleSrc, (texture) => {
      this.applyTextureEncoding(texture)

      const greenSquiggleMaterial = squiggleMaterial.clone() //new THREE.SpriteMaterial({ map: texture })
      greenSquiggleMaterial.map = texture

      const { width, height } = greenSquiggleMaterial.map?.image || {
        width: 0,
        height: 0,
      }

      const scaleOffset = 4

      const greenSquiggle = new THREE.Sprite(greenSquiggleMaterial)
      greenSquiggle.center.set(0.0, 0.0)
      greenSquiggle.scale.set(
        width / (pixelRatio * scaleOffset),
        height / (pixelRatio * scaleOffset),
        1
      )
      greenSquiggle?.position.set(-wWidth - 25, -wHeight / 4, 1)

      this.group.add(greenSquiggle)
    })

    // Lines
    this.loader.load(linesSquiggleSrc, (texture) => {
      this.applyTextureEncoding(texture)

      const linesSquiggleMaterial = squiggleMaterial.clone()
      linesSquiggleMaterial.map = texture

      const { width, height } = linesSquiggleMaterial.map?.image || {
        width: 0,
        height: 0,
      }

      const scaleOffset = 4

      const linesSquiggle = new THREE.Sprite(linesSquiggleMaterial)
      linesSquiggle.center.set(1.0, 0.0)
      linesSquiggle.scale.set(
        width / (pixelRatio * scaleOffset),
        height / (pixelRatio * scaleOffset),
        1
      )
      linesSquiggle?.position.set(wWidth - 10, -wHeight / 4, 1)

      this.group.add(linesSquiggle)
    })

    // Lines 2
    this.loader.load(lines2SquiggleSrc, (texture) => {
      this.applyTextureEncoding(texture)

      const lines2SquiggleMaterial = squiggleMaterial.clone() //new THREE.SpriteMaterial({ map: texture })
      lines2SquiggleMaterial.map = texture

      const { width, height } = lines2SquiggleMaterial.map?.image || {
        width: 0,
        height: 0,
      }

      const scaleOffset = 4

      const lines2Squiggle = new THREE.Sprite(lines2SquiggleMaterial)
      lines2Squiggle.center.set(0.0, 1.0)
      lines2Squiggle.scale.set(
        width / (pixelRatio * scaleOffset),
        height / (pixelRatio * scaleOffset),
        1
      )
      lines2Squiggle?.position.set(-wWidth - 10, wHeight / 1.5, 1)

      this.group.add(lines2Squiggle)
    })

    // Orange
    this.loader.load(orangeSquiggleSrc, (texture) => {
      this.applyTextureEncoding(texture)

      const orangeSquiggleMaterial = squiggleMaterial.clone() //new THREE.SpriteMaterial({ map: texture })
      orangeSquiggleMaterial.map = texture

      const { width, height } = orangeSquiggleMaterial.map?.image || {
        width: 0,
        height: 0,
      }

      const scaleOffset = 2.5

      const orangeSquiggle = new THREE.Sprite(orangeSquiggleMaterial)
      orangeSquiggle.center.set(1.0, 1.0)
      orangeSquiggle.scale.set(
        width / (pixelRatio * scaleOffset),
        height / (pixelRatio * scaleOffset),
        1
      )
      orangeSquiggle?.position.set(wWidth + 40, wHeight / 1.2, 1)

      this.group.add(orangeSquiggle)
    })

    // Pink
    this.loader.load(pinkSquiggleSrc, (texture) => {
      this.applyTextureEncoding(texture)

      const pinkSquiggleMaterial = squiggleMaterial.clone() //new THREE.SpriteMaterial({ map: texture })
      pinkSquiggleMaterial.map = texture

      const { width, height } = pinkSquiggleMaterial.map?.image || {
        width: 0,
        height: 0,
      }

      const scaleOffset = 3

      const pinkSquiggle = new THREE.Sprite(pinkSquiggleMaterial)
      pinkSquiggle.center.set(1, 1.0)
      pinkSquiggle.scale.set(
        width / (pixelRatio * scaleOffset),
        height / (pixelRatio * scaleOffset),
        1
      )
      pinkSquiggle?.position.set(wWidth + 40, wHeight / 1.9, 1)

      this.group.add(pinkSquiggle)
    })

    // Pink 2
    this.loader.load(pink2SquiggleSrc, (texture) => {
      this.applyTextureEncoding(texture)

      const pink2SquiggleMaterial = squiggleMaterial.clone() //new THREE.SpriteMaterial({ map: texture })
      pink2SquiggleMaterial.map = texture

      const { width, height } = pink2SquiggleMaterial.map?.image || {
        width: 0,
        height: 0,
      }

      const scaleOffset = 4

      const pink2Squiggle = new THREE.Sprite(pink2SquiggleMaterial)
      pink2Squiggle.center.set(0.0, 0.0)
      pink2Squiggle.scale.set(
        width / (pixelRatio * scaleOffset),
        height / (pixelRatio * scaleOffset),
        1
      )
      pink2Squiggle?.position.set(-wWidth - 10, -wHeight / 1.3, 1)

      this.group.add(pink2Squiggle)
    })

    // Yellow
    // this.loader.load(yellowSquiggleSrc, (texture) => {
    //   this.applyTextureEncoding(texture)

    //   const yellowSquiggleMaterial = squiggleMaterial.clone() //new THREE.SpriteMaterial({ map: texture })
    //   yellowSquiggleMaterial.map = texture

    //   const { width, height } = yellowSquiggleMaterial.map?.image || {
    //     width: 0,
    //     height: 0,
    //   }

    //   const scaleOffset = 2.5

    //   this.yellowSquiggle = new THREE.Sprite(yellowSquiggleMaterial)
    //   this.yellowSquiggle.center.set(0.0, 0.0)
    //   this.yellowSquiggle.scale.set(
    //     width / (pixelRatio * scaleOffset),
    //     height / (pixelRatio * scaleOffset),
    //     1
    //   )
    //   this.yellowSquiggle?.position.set(-wWidth / 3, -wHeight / 1.1, 1)

    //   this.group.add(this.yellowSquiggle)
    // })
  }
}
